import {
    GetAccountsResponse,
    GetApplicationResponse
} from '@iwoca/lapi-client/edge';
import { isFlexi24Duration } from '../flexi24/getIsFlexi24Duration';
import { MY_OFFERS_URL } from '../myOffers/MyOffers.sagas';

type FormalOffer = NonNullable<
    NonNullable<GetAccountsResponse['data']>['accounts'][0]['formal_offers']
>[number];

type Accounts = NonNullable<GetAccountsResponse['data']>['accounts'];

const productTypeToPathnameMapping = {
    credit_facility: '/',
    flexi24: '/flexi-24/',
    business_loan: '/business-loan/', //lapi client has business_loan
    term_loan: '/business-loan/',
    iwocapay: '/iwocapay/',
    cbils: '/CBILS/',
    recovery_loan: '/recovery-loan-scheme/',
    merchant_cash_advance: '/revenue-based-loan/'
};

export const getDefaultDashboardLocation = (
    applicationResponse: GetApplicationResponse,
    accountsResponse: GetAccountsResponse
): Partial<Location> | null => {
    //redirect iwocapay only accounts
    if (
        !hasActiveNonIwocaPayApplication(applicationResponse) &&
        (hasExclusivelyIwocaPayAccount(accountsResponse, applicationResponse) ||
            hasValidIwocaPayFormalOffer(accountsResponse))
    ) {
        return null;
    } else if (
        //if accounts is empty assume application state
        (accountsResponse.data &&
            accountsResponse.data.accounts.length === 0) ||
        hasUnFundedAccount(accountsResponse, applicationResponse) ||
        hasNewFormalOffer(accountsResponse)
    ) {
        return { pathname: MY_OFFERS_URL };
    } else return getLocationForCurrentAccountProductType(accountsResponse);
};

const getLocationForCurrentAccountProductType = (
    accountsResponse: GetAccountsResponse
): Partial<Location> | null => {
    const accounts = accountsResponse.data!.accounts;

    //redirect to ongoing accounts first, else default to first account in the list
    for (const account of accounts) {
        if (account.account_id && account.loan_status !== 'ended') {
            return {
                pathname:
                    productTypeToPathnameMapping[
                        getAccountProductType(account)!
                    ]
            };
        }
    }

    return {
        pathname:
            productTypeToPathnameMapping[getAccountProductType(accounts[0])!]
    };
};

const getAccountProductType = (
    account: Accounts[0]
): Accounts[0]['product_type'] => {
    let productType = account.product_type;
    if (
        productType === 'credit_facility' &&
        isFlexi24Duration(account.duration!)
    ) {
        productType = 'flexi24';
    }
    return productType;
};

const hasUnFundedAccount = (
    accountsResponse: GetAccountsResponse,
    applicationResponse: GetApplicationResponse
): boolean => {
    const accounts = accountsResponse.data!.accounts;

    const formalOffers: FormalOffer[] = getFormalOffersFromAccounts(accounts);

    const hasOngoingFunding = hasOngoingFundingForFormalOfferProductType(
        accounts,
        formalOffers
    );

    if (isIwocaPayCrossSell(accountsResponse, applicationResponse)) {
        return true;
    } // don't include active iwocaPay as we support x-product
    else {
        return accounts.some((account) => {
            if (!account.account_id && !hasOngoingFunding) return true;
            if (account.loan_status && account.loan_status !== 'ended')
                return false;
            else return false;
        });
    }
};

const hasNewFormalOffer = (accountsResponse: GetAccountsResponse): boolean => {
    for (const account of accountsResponse.data!.accounts) {
        if (account.formal_offers!.length > 0) {
            for (const formalOffer of account.formal_offers!) {
                if (account.loan_status === 'ended' && formalOffer.is_valid) {
                    return true;
                }
            }
        }
    }
    return false;
};

const hasOngoingFundingForFormalOfferProductType = (
    accounts: Accounts,
    formalOffers: FormalOffer[]
): boolean => {
    const flexiOfferTypes = ['flexi24', 'credit_facility'];

    if (formalOffers.length === 0) return false;
    if (accounts.length === 0) return false;

    for (const formalOffer of formalOffers) {
        for (const account of accounts) {
            if (
                account.account_id &&
                (formalOffer.product_type === account.product_type ||
                    //treat all flexi product types as the same type
                    (flexiOfferTypes.includes(formalOffer.product_type!) &&
                        flexiOfferTypes.includes(account.product_type!))) &&
                account.loan_status !== 'ended'
            ) {
                return true;
            }
        }
    }
    return false;
};

const isIwocaPayCrossSell = (
    accountsResponse: GetAccountsResponse,
    applicationResponse: GetApplicationResponse
): boolean => {
    if (!applicationResponse.data || !applicationResponse.data.status)
        return false;

    const accounts = accountsResponse.data!.accounts;

    return accounts.some((account) => {
        if (account.account_id && account.product_type !== 'iwocapay')
            return false;
        if (hasActiveNonIwocaPayApplication(applicationResponse)) {
            return true;
        } else return false;
    });
};

const getFormalOffersFromAccounts = (
    accounts: Accounts | null
): FormalOffer[] => {
    const formalOffers: FormalOffer[] = [];
    if (accounts && accounts.length > 0) {
        for (const account of accounts) {
            if (account.formal_offers!.length > 0) {
                formalOffers.push(...account.formal_offers!);
            }
        }
    }
    return formalOffers;
};

const hasExclusivelyIwocaPayAccount = (
    accountsResponse: GetAccountsResponse,
    applicationResponse: GetApplicationResponse
): boolean => {
    if (!applicationResponse.data) return false;

    const accounts = accountsResponse.data!.accounts;

    if (accounts.length > 0) {
        const nonIwocaPayAccount = accounts.find(
            (account) =>
                account.product_type && account.product_type !== 'iwocapay'
        );
        if (nonIwocaPayAccount) {
            return false;
        } else return true;
    } else if (hasIwocaPayApplication(applicationResponse)) {
        return true;
    } else return false;
};

export const hasIwocaPayApplication = (
    applicationResponse: GetApplicationResponse
): boolean => {
    if (!applicationResponse.data) return false;
    // TODO: Requested products is always in the response, but some tests
    // are failing due to bad fixtures.
    if (!applicationResponse.data.requested_products) return false;

    for (const requestedProducts of applicationResponse.data
        .requested_products) {
        //ignore offered applications
        if (
            !requestedProducts.suggested_product!.product_type ||
            !requestedProducts.suggested_product!.duration ||
            applicationResponse.data.status === 'offered'
        )
            continue;

        if (requestedProducts.suggested_product!.product_type === 'iwocapay') {
            return true;
        }
    }
    return false;
};

const hasActiveNonIwocaPayApplication = (
    applicationResponse: GetApplicationResponse
): boolean => {
    if (!applicationResponse.data) return false;
    // TODO: Requested products is always in the response, but some tests
    // are failing due to bad fixtures.
    if (!applicationResponse.data.requested_products) return false;

    for (const requestedProducts of applicationResponse.data
        .requested_products) {
        //ignore offered, deferred and declined applications
        if (
            !requestedProducts.suggested_product!.product_type ||
            !requestedProducts.suggested_product!.duration ||
            applicationResponse.data.status !== 'open'
        )
            continue;

        if (requestedProducts.suggested_product!.product_type !== 'iwocapay') {
            return true;
        }
    }
    return false;
};

export const hasValidIwocaPayFormalOffer = (
    accountsResponse: GetAccountsResponse
): boolean => {
    for (const account of accountsResponse.data!.accounts) {
        if (account.formal_offers!.length === 0) {
            return false;
        } else {
            if (account.formal_offers![0].product_type === 'iwocapay') {
                return true;
            }
        }
    }
    return false;
};
